import React, { useMemo } from 'react';
import { View, Text, Image } from 'react-native';
import { formatCurrency as formatCurrencyDefault } from '../utils/formatters';

// Local helper to spell out totals (copied from screen implementation)
const numberToWords = (num) => {
  try {
    const a = ['','one','two','three','four','five','six','seven','eight','nine','ten','eleven','twelve','thirteen','fourteen','fifteen','sixteen','seventeen','eighteen','nineteen'];
    const b = ['','', 'twenty','thirty','forty','fifty','sixty','seventy','eighty','ninety'];
    const s = (n) => n < 20 ? a[n] : b[Math.floor(n/10)] + (n%10 ? '-' + a[n%10] : '');
    const g = (n) => n < 100 ? s(n) : (a[Math.floor(n/100)] + ' hundred ' + (n%100 ? s(n%100) : '')).trim();
    const th = Math.floor(num/1000), r = num%1000; const out = (th? g(th)+' thousand ' : '') + g(r);
    return out.trim().replace(/\s+/g,' ');
  } catch (_) { return String(num); }
};

/**
 * QuotationPrint
 * Renders an A4 print-ready quotation page (matches on-screen preview templates)
 * Props expect normalized structures (use EmailQuoteModal or QuotationMaker to normalize row).
 */
export default function QuotationPrint({
  template = 'classic',
  quote = { title: 'Quotation', number: '', quoteDate: '', validUntil: '', currency: 'INR', dueDate: '' },
  seller = {},
  client = {},
  shipping = { enabled: false, address: '', city: '', postalCode: '' },
  items = [],
  charges = { addlCharges: 0, discount: 0, roundingMode: 'none', showTotalInWords: true },
  columns = { showHSN: true, showGST: true, showImage: false },
  notes = '',
  terms = '',
  contactDetails = '',
  additionalInfo = '',
  logoUri = '',
  showDueDate = false,
  signatureUri = '',
}) {
  const isClassic = template === 'classic';
  const isEmerald = template === 'emerald';
  const isMinimal = template === 'minimal';
  const isSlate = template === 'slate';

  const themes = {
    classic: {
      accent: '#2563EB',
      accentSoft: '#DBEAFE',
      headerTitleColor: '#111827',
      brandTextColor: '#0f172a',
      blockBorder: '#e5e7eb',
      blockBg: '#ffffff',
      tableHeaderBg: '#DBEAFE',
      tableHeaderText: '#374151',
      grandColor: '#374151',
    },
    emerald: {
      accent: '#6D28D9',
      accentSoft: '#EDE9FE',
      headerTitleColor: '#6D28D9',
      brandTextColor: '#0f172a',
      blockBorder: '#6D28D9',
      blockBg: '#F5F3FF',
      tableHeaderBg: '#6D28D9',
      tableHeaderText: '#ffffff',
      grandColor: '#6D28D9',
    },
    minimal: {
      accent: '#111827',
      accentSoft: '#ffffff',
      headerTitleColor: '#111827',
      brandTextColor: '#111827',
      blockBorder: '#e5e7eb',
      blockBg: '#ffffff',
      tableHeaderBg: '#ffffff',
      tableHeaderText: '#111827',
      grandColor: '#111827',
    },
    slate: {
      accent: '#0F172A',
      accentSoft: '#1F2937',
      headerTitleColor: '#0F172A',
      brandTextColor: '#0f172a',
      blockBorder: '#e5e7eb',
      blockBg: '#ffffff',
      tableHeaderBg: '#1F2937',
      tableHeaderText: '#ffffff',
      grandColor: '#0F172A',
    },
  };
  const theme = isEmerald ? themes.emerald : isMinimal ? themes.minimal : isSlate ? themes.slate : themes.classic;

  const summary = useMemo(() => {
    const subtotal = items.reduce((acc, it) => acc + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
    const taxTotal = items.reduce((acc, it) => acc + ((Number(it.qty) || 0) * (Number(it.rate) || 0)) * ((Number(it.gstRate) || 0) / 100), 0);
    const discount = Number(charges.discount) || 0;
    const addl = Number(charges.addlCharges) || 0;
    let total = Math.max(0, subtotal - discount + addl + taxTotal);
    if (charges.roundingMode === 'up') total = Math.ceil(total);
    else if (charges.roundingMode === 'down') total = Math.floor(total);
    return { subtotal, taxTotal, discount, addl, total };
  }, [items, charges]);

  // Currency formatting aligned with screen implementation
  const currencySymbols = { INR: '₹', USD: '$', EUR: '€' };
  const currencySymbol = currencySymbols[quote.currency] || '₹';
  const formatCurrency = (n) => `${currencySymbol}${(Number(n) || 0).toFixed(2)}`;

  const printStyles = {
    page: { width: 794, minHeight: 1123, backgroundColor: '#ffffff', padding: 24, borderWidth: 1, borderColor: '#e5e7eb', position: 'relative' },
    headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
    brand: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    brandText: { fontSize: 18, fontWeight: '700', color: theme.brandTextColor },
    invMetaRight: { alignItems: 'flex-end' },
    invTitle: { fontSize: isMinimal ? 24 : 22, fontWeight: '800', color: theme.headerTitleColor },
    invNo: { fontSize: 14, color: '#374151', marginTop: 4 },
    block: { borderWidth: 1, borderColor: theme.blockBorder, backgroundColor: theme.blockBg, borderRadius: 8, padding: 12, marginTop: 12 },
    blockTitle: { fontSize: 14, fontWeight: '700', color: '#111827', marginBottom: 8 },
    twoCol: { flexDirection: 'row', gap: 12 },
    col: { flex: 1 },
    label: { fontSize: 11, color: '#6b7280' },
    value: { fontSize: 12, color: '#0f172a', fontWeight: '600', marginBottom: 4 },
    tableHeader: { flexDirection: 'row', backgroundColor: theme.tableHeaderBg, borderTopLeftRadius: 8, borderTopRightRadius: 8, borderBottomWidth: (isEmerald || isMinimal) ? 0 : 1, borderBottomColor: '#e5e7eb', borderTopWidth: isMinimal ? 1 : 0, borderTopColor: isMinimal ? '#e5e7eb' : undefined },
    th: { paddingVertical: 8, paddingHorizontal: 6, fontSize: 11, fontWeight: '700', color: (isEmerald || isSlate) ? '#ffffff' : theme.tableHeaderText, textTransform: isMinimal ? 'uppercase' : 'none', letterSpacing: isMinimal ? 1 : 0 },
    row: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#e5e7eb', alignItems: 'flex-start' },
    tdBox: { paddingVertical: 8, paddingHorizontal: 6 },
    td: { fontSize: 12, color: '#111827' },
    totals: { marginTop: 12, gap: 6 },
    totalsRow: { flexDirection: 'row', justifyContent: 'space-between' },
    totalsLabel: { fontSize: 12, color: '#374151' },
    totalsValue: { fontSize: 12, fontWeight: '700', color: '#0f172a' },
    grandRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
    grandLabel: { fontSize: 14, fontWeight: '800', color: '#111827' },
    grandValue: { fontSize: 16, fontWeight: '800', color: theme.grandColor },
    small: { fontSize: 11, color: '#6b7280' },
    signatureRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 24 },
    sigBlock: { width: 220, alignItems: 'center' },
    sigLine: { height: 1, backgroundColor: '#e5e7eb', alignSelf: 'stretch', marginTop: 24 },
    footerText: { position: 'absolute', bottom: 8, right: 12, fontSize: 9, color: '#9CA3AF' },
  };

  const hasHSN = !!columns.showHSN;
  const hasGST = !!columns.showGST;
  const hasImage = !!columns.showImage;

  return (
    <View style={printStyles.page}>
      <View style={printStyles.headerRow}>
        {isEmerald ? (
          <>
            <View style={{ flex: 1 }}>
              <Text style={[printStyles.invTitle, { color: theme.accent }]}>Quotation</Text>
              <Text style={printStyles.small}>Quotation No: {quote.number || '-'}</Text>
              <Text style={printStyles.small}>Quotation Date: {quote.quoteDate || '-'}</Text>
              {!!quote.validUntil && (<Text style={printStyles.small}>Valid Until: {quote.validUntil}</Text>)}
              {showDueDate && !!quote.dueDate && (<Text style={printStyles.small}>Due: {quote.dueDate}</Text>)}
            </View>
            <View style={[printStyles.brand, { alignSelf: 'flex-start' }]}> 
              {logoUri ? (
                <Image source={{ uri: logoUri }} style={{ width: 48, height: 48, borderRadius: 8 }} />
              ) : (
                <View style={{ width: 48, height: 48, borderRadius: 8, backgroundColor: theme.accentSoft, borderWidth: 1, borderColor: theme.accent }} />
              )}
              <Text style={printStyles.brandText}>{seller.businessName || 'Your Business'}</Text>
            </View>
          </>
        ) : isSlate ? (
          <View style={{ backgroundColor: theme.accent, borderRadius: 10, padding: 14, marginBottom: 2, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <View style={[printStyles.brand, { gap: 10 }]}> 
              {logoUri ? (
                <Image source={{ uri: logoUri }} style={{ width: 48, height: 48, borderRadius: 8 }} />
              ) : (
                <View style={{ width: 48, height: 48, borderRadius: 8, backgroundColor: theme.accentSoft, borderWidth: 1, borderColor: '#FFFFFF22' }} />
              )}
              <Text style={[printStyles.brandText, { color: '#ffffff' }]}>{seller.businessName || 'Your Business'}</Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={[printStyles.invTitle, { color: '#ffffff' }]}>{quote.title || 'Quotation'}</Text>
              <Text style={{ fontSize: 12, color: '#CBD5E1', marginTop: 4 }}>No: {quote.number || '-'}</Text>
              <Text style={{ fontSize: 12, color: '#CBD5E1' }}>Date: {quote.quoteDate || '-'}</Text>
            </View>
          </View>
        ) : isMinimal ? (
          <>
            <View style={{ flex: 1 }}>
              <Text style={[printStyles.invTitle]}>{quote.title || 'Quotation'}</Text>
              <View style={{ height: 2, backgroundColor: theme.accent, borderRadius: 1, marginTop: 6, marginBottom: 2 }} />
              <Text style={printStyles.small}>No: {quote.number || '-'}</Text>
              <Text style={printStyles.small}>Date: {quote.quoteDate || '-'}</Text>
            </View>
            <View style={printStyles.brand}>
              {logoUri ? (
                <Image source={{ uri: logoUri }} style={{ width: 40, height: 40, borderRadius: 8 }} />
              ) : (
                <View style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#ffffff', borderWidth: 1, borderColor: '#e5e7eb' }} />
              )}
              <Text style={printStyles.brandText}>{seller.businessName || 'Your Business'}</Text>
            </View>
          </>
        ) : (
          <>
            <View style={printStyles.brand}>
              {logoUri ? (
                <Image source={{ uri: logoUri }} style={{ width: 48, height: 48, borderRadius: 8 }} />
              ) : (
                <View style={{ width: 48, height: 48, borderRadius: 8, backgroundColor: theme.accentSoft, borderWidth: 1, borderColor: theme.accent }} />
              )}
              <Text style={printStyles.brandText}>{seller.businessName || 'Your Business'}</Text>
            </View>
            <View style={printStyles.invMetaRight}>
              <Text style={printStyles.invTitle}>{quote.title || 'Quotation'}</Text>
              <Text style={printStyles.invNo}>No: {quote.number || '-'}</Text>
              <Text style={printStyles.small}>Date: {quote.quoteDate || '-'}</Text>
              {!!quote.validUntil && (<Text style={printStyles.small}>Valid Until: {quote.validUntil}</Text>)}
              {showDueDate && !!quote.dueDate && (<Text style={printStyles.small}>Due: {quote.dueDate}</Text>)}
            </View>
          </>
        )}
      </View>

      <View style={[printStyles.block, printStyles.twoCol, isEmerald && { backgroundColor: theme.accentSoft, borderColor: theme.accent }]}> 
        <View style={printStyles.col}>
          <Text style={printStyles.blockTitle}>Quoted By</Text>
          <Text style={printStyles.value}>{seller.businessName || '-'}</Text>
          {!!seller.address && (<Text style={printStyles.small}>{seller.address}</Text>)}
          <Text style={printStyles.small}>{seller.city || ''} {seller.postalCode || ''}</Text>
          {!!seller.country && (<Text style={printStyles.small}>{seller.country}</Text>)}
          {!!seller.gstin && (<Text style={printStyles.small}>GSTIN: {seller.gstin}</Text>)}
          {!!seller.pan && (<Text style={printStyles.small}>PAN: {seller.pan}</Text>)}
          {!!seller.email && (<Text style={printStyles.small}>Email: {seller.email}</Text>)}
          {!!seller.phone && (<Text style={printStyles.small}>Phone: {seller.phone}</Text>)}
          {(seller.customFields || []).map((f, idx) => (
            <Text key={idx} style={printStyles.small}>{(f.label || '—') + ': ' + (f.value || '—')}</Text>
          ))}
          {!!seller.state && (<Text style={[printStyles.small, { marginTop: 6 }]}>Place of Supply: {seller.state}</Text>)}
          {!!seller.country && (<Text style={printStyles.small}>Country of Supply: {seller.country}</Text>)}
        </View>
        <View style={printStyles.col}>
          <Text style={printStyles.blockTitle}>Quoted To</Text>
          <Text style={printStyles.value}>{client.businessName || '-'}</Text>
          {!!client.address && (<Text style={printStyles.small}>{client.address}</Text>)}
          <Text style={printStyles.small}>{client.city || ''} {client.postalCode || ''}</Text>
          {!!client.country && (<Text style={printStyles.small}>{client.country}</Text>)}
          {!!client.gstin && (<Text style={printStyles.small}>GSTIN: {client.gstin}</Text>)}
          {!!client.pan && (<Text style={printStyles.small}>PAN: {client.pan}</Text>)}
          {!!client.email && (<Text style={printStyles.small}>Email: {client.email}</Text>)}
          {!!client.phone && (<Text style={printStyles.small}>Phone: {client.phone}</Text>)}
          {(client.customFields || []).map((f, idx) => (
            <Text key={idx} style={printStyles.small}>{(f.label || '—') + ': ' + (f.value || '—')}</Text>
          ))}
          {!!client.state && (<Text style={[printStyles.small, { marginTop: 6 }]}>Place of Supply: {client.state}</Text>)}
          {!!client.country && (<Text style={printStyles.small}>Country of Supply: {client.country}</Text>)}
        </View>
      </View>

      {shipping.enabled && (
        <View style={[printStyles.block]}>
          <Text style={printStyles.blockTitle}>Shipping Details</Text>
          <Text style={printStyles.small}>{shipping.address}</Text>
          <Text style={printStyles.small}>{shipping.city} {shipping.postalCode}</Text>
        </View>
      )}

      {/* Items table */}
      <View style={printStyles.block}>
        <View style={printStyles.tableHeader}>
          {hasImage && (<Text style={[printStyles.th, { flex: 0.8 }]}>Image</Text>)}
          <Text style={[printStyles.th, { flex: 2 }]}>Item</Text>
          {hasHSN && (<Text style={[printStyles.th, { flex: 1 }]}>HSN/SAC</Text>)}
          {hasGST && (<Text style={[printStyles.th, { flex: 1 }]}>GST %</Text>)}
          <Text style={[printStyles.th, { flex: 1 }]}>Qty</Text>
          <Text style={[printStyles.th, { flex: 1 }]}>Rate</Text>
          <Text style={[printStyles.th, { flex: 1 }]}>Total</Text>
        </View>
        {items.map((line, idx) => {
          const baseAmount = (Number(line.qty) || 0) * (Number(line.rate) || 0);
          const taxAmt = baseAmount * ((Number(line.gstRate) || 0) / 100);
          const lineTotal = baseAmount + taxAmt;
          const rowStyle = [
            printStyles.row,
            isSlate && (idx % 2 === 1) ? { backgroundColor: '#F8FAFC' } : null,
            isMinimal ? { borderBottomColor: '#f3f4f6' } : null,
          ];
          return (
            <View key={line.id || idx} style={rowStyle}>
              {hasImage && (
                <View style={[printStyles.tdBox, { flex: 0.8 }]}> 
                  {!!line.imageUri ? (<Image source={{ uri: line.imageUri }} style={{ width: 32, height: 32, borderRadius: 4 }} />) : (<Text style={printStyles.small}>—</Text>)}
                </View>
              )}
              <View style={[printStyles.tdBox, { flex: 2 }]}> 
                <Text style={printStyles.td}>{line.name || '—'}</Text>
                {!!line.description && (<Text style={printStyles.small}>{line.description}</Text>)}
              </View>
              {hasHSN && (
                <View style={[printStyles.tdBox, { flex: 1 }]}> 
                  <Text style={printStyles.td}>{line.hsnSac || '—'}</Text>
                </View>
              )}
              {hasGST && (
                <View style={[printStyles.tdBox, { flex: 1 }]}> 
                  <Text style={printStyles.td}>{String(line.gstRate || 0)}%</Text>
                </View>
              )}
              <View style={[printStyles.tdBox, { flex: 1 }]}> 
                <Text style={printStyles.td}>{String(line.qty || 0)}</Text>
              </View>
              <View style={[printStyles.tdBox, { flex: 1 }]}> 
                <Text style={printStyles.td}>{formatCurrency(Number(line.rate) || 0)}</Text>
              </View>
              <View style={[printStyles.tdBox, { flex: 1 }]}> 
                <Text style={printStyles.td}>{formatCurrency(lineTotal)}</Text>
              </View>
            </View>
          );
        })}
      </View>

      {/* Totals area */}
      <View style={printStyles.block}>
        <View style={printStyles.totals}>
          <View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Subtotal</Text><Text style={printStyles.totalsValue}>{formatCurrency(summary.subtotal)}</Text></View>
          <View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Tax</Text><Text style={printStyles.totalsValue}>{formatCurrency(summary.taxTotal)}</Text></View>
          {!!charges.addlCharges && (<View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Additional Charges</Text><Text style={printStyles.totalsValue}>{formatCurrency(summary.addl)}</Text></View>)}
          {!!charges.discount && (<View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Discount</Text><Text style={printStyles.totalsValue}>- {formatCurrency(summary.discount)}</Text></View>)}
        </View>
        <View style={printStyles.grandRow}><Text style={printStyles.grandLabel}>Grand Total ({quote.currency})</Text><Text style={[printStyles.grandValue]}>{formatCurrency(summary.total)}</Text></View>
        {charges.showTotalInWords && (
          <Text style={[printStyles.small, { marginTop: 6 }]}>Amount in words: {numberToWords(Math.round(summary.total))} {quote.currency}</Text>
        )}
      </View>

      {(terms || notes || contactDetails || additionalInfo) && (
        <View style={printStyles.block}>
          {!!terms && (<><Text style={printStyles.blockTitle}>Terms & Conditions</Text><Text style={printStyles.small}>{terms}</Text></>)}
          {!!notes && (<><Text style={[printStyles.blockTitle, { marginTop: 8 }]}>Notes</Text><Text style={printStyles.small}>{notes}</Text></>)}
          {!!additionalInfo && (<><Text style={[printStyles.blockTitle, { marginTop: 8 }]}>Additional Info</Text><Text style={printStyles.small}>{additionalInfo}</Text></>)}
          {!!contactDetails && (<><Text style={[printStyles.blockTitle, { marginTop: 8 }]}>Contact</Text><Text style={printStyles.small}>{contactDetails}</Text></>)}
        </View>
      )}

      {/* Signature blocks */}
      <View style={printStyles.signatureRow}>
        <View style={printStyles.sigBlock}>
          <Text style={printStyles.small}>Authorised Signature</Text>
          {!!signatureUri && (<Image source={{ uri: signatureUri }} style={{ width: 200, height: 80, resizeMode: 'contain', marginTop: 8 }} />)}
          <View style={printStyles.sigLine} />
        </View>
        <View style={printStyles.sigBlock}>
          <Text style={printStyles.small}>Receiver’s Signature</Text>
          <View style={printStyles.sigLine} />
        </View>
      </View>

      <Text style={printStyles.footerText}>Quotation generated by modernmunshiji.com</Text>
    </View>
  );
}